import { useEffect } from "react";

/**
 * Lightweight SEO helper — directly manipulates document.head so
 * Google (which renders JS) and OG/Twitter scrapers see proper tags.
 *
 * Props:
 *   - title          : string (full <title>)
 *   - description    : string
 *   - canonicalPath  : string (e.g. "/inventory/abc")
 *   - lang           : "fr" | "en"
 *   - image          : absolute or root-relative URL
 *   - type           : "website" | "product" | "article"
 *   - jsonLd         : object | array — JSON-LD structured data
 *   - alternates     : { fr: "/path", en: "/path?lang=en" }
 */
export default function Seo({
  title,
  description,
  canonicalPath,
  lang = "fr",
  image,
  type = "website",
  jsonLd,
  alternates,
}) {
  useEffect(() => {
    const origin = window.location.origin;
    const url = canonicalPath ? `${origin}${canonicalPath}` : window.location.href;

    if (title) document.title = title;
    document.documentElement.lang = lang;

    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("content", attrs.content || "");
      el.setAttribute("data-seo", "1");
    };

    const upsertLink = (rel, href, extraAttrs = {}) => {
      let el = document.head.querySelector(
        `link[rel="${rel}"]${extraAttrs.hreflang ? `[hreflang="${extraAttrs.hreflang}"]` : ""}`
      );
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      el.setAttribute("data-seo", "1");
    };

    if (description) {
      upsertMeta('meta[name="description"]', {
        create: { name: "description" },
        content: description,
      });
    }

    // Open Graph
    if (title)
      upsertMeta('meta[property="og:title"]', { create: { property: "og:title" }, content: title });
    if (description)
      upsertMeta('meta[property="og:description"]', {
        create: { property: "og:description" },
        content: description,
      });
    upsertMeta('meta[property="og:type"]', { create: { property: "og:type" }, content: type });
    upsertMeta('meta[property="og:url"]', { create: { property: "og:url" }, content: url });
    upsertMeta('meta[property="og:locale"]', {
      create: { property: "og:locale" },
      content: lang === "en" ? "en_CA" : "fr_CA",
    });
    if (image)
      upsertMeta('meta[property="og:image"]', { create: { property: "og:image" }, content: image });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      create: { name: "twitter:card" },
      content: image ? "summary_large_image" : "summary",
    });
    if (title)
      upsertMeta('meta[name="twitter:title"]', { create: { name: "twitter:title" }, content: title });
    if (description)
      upsertMeta('meta[name="twitter:description"]', {
        create: { name: "twitter:description" },
        content: description,
      });
    if (image)
      upsertMeta('meta[name="twitter:image"]', { create: { name: "twitter:image" }, content: image });

    // Canonical
    upsertLink("canonical", url);

    // hreflang alternates
    if (alternates) {
      Object.entries(alternates).forEach(([altLang, altPath]) => {
        upsertLink("alternate", `${origin}${altPath}`, { hreflang: altLang });
      });
      upsertLink("alternate", url, { hreflang: "x-default" });
    }

    // JSON-LD structured data
    const oldLd = document.head.querySelectorAll('script[type="application/ld+json"][data-seo="1"]');
    oldLd.forEach((n) => n.parentNode.removeChild(n));
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo", "1");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  }, [title, description, canonicalPath, lang, image, type, jsonLd, alternates]);

  return null;
}
